import React from 'react';
import { Box, Tooltip } from '@chakra-ui/react';
import { chakraComponents } from 'chakra-react-select';

export interface PocSelectOption {
  value: string;
  label: string;
}

// Grupo de POCs no select: a sintética vira cabeçalho (não-selecionável) e as
// analíticas (selecionáveis) ficam sob ela. react-select renderiza o `label`
// como GroupHeading automaticamente.
export interface PocSelectGroup {
  label: string;
  options: PocSelectOption[];
}

export const fmtValor = (v?: number | null) =>
  v == null || Number.isNaN(v)
    ? '—'
    : v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Moeda com simbolo: nos KPIs o numero aparece sozinho e sem "R$" fica solto.
export const fmtMoeda = (v?: number | null) =>
  v == null || Number.isNaN(v) ? '—' : `R$ ${fmtValor(v)}`;

// Corpo da fonte por comprimento do texto: o KPI nao arredonda e o card corta
// sem ellipsis, entao o valor precisa caber inteiro.
export const fonteValorKpi = (texto: string) => {
  if (texto.length >= 17) return { base: '12px', md: '15px', lg: '22px' };
  if (texto.length >= 14) return { base: '13px', md: '17px', lg: '22px' };
  if (texto.length >= 12) return { base: '15px', md: '19px', lg: '22px' };
  return { base: '18px', md: '22px' };
};

export const fmtData = (iso?: string | null) => {
  if (!iso) return '—';
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
};

export const fmtDoc = (doc?: string | null) => {
  if (!doc) return '';
  const d = doc.replace(/\D/g, '');
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return doc;
};

// Status do lançamento -> colorScheme do Chakra (badge) e hex (barra/tint).
export const STATUS_BADGE: Record<string, { cor: string; label: string }> = {
  conciliado: { cor: 'green', label: 'conciliado' },
  pendente: { cor: 'orange', label: 'pendente' },
  revisao: { cor: 'purple', label: 'revisão' },
};

export const STATUS_ACCENT: Record<string, string> = {
  conciliado: '#10B981',
  pendente: '#F59E0B',
  revisao: '#8B5CF6',
};

// Cor do dot de confiança: alta=verde, média=âmbar, baixa=vermelho.
export function corConfianca(pct: number | null): string {
  if (pct == null) return '#94A3B8';
  if (pct >= 85) return '#10B981';
  if (pct >= 60) return '#F59E0B';
  return '#EF4444';
}

// Chip D/C do lado contábil da contrapartida. C (crédito) = vermelho, D
// (débito) = verde — invertido em relação ao sinal do valor de propósito,
// porque a contrapartida é o lado oposto do banco. Tooltip explica o lado.
export function PocLadoBadge({ lado }: { lado: 'C' | 'D' }) {
  const cred = lado === 'C';
  return (
    <Box
      as="span"
      ml="6px"
      flexShrink={0}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      w="18px"
      h="18px"
      borderRadius="md"
      fontSize="10px"
      fontWeight="700"
      bg={cred ? 'rgba(239, 68, 68, 0.12)' : 'rgba(16, 185, 129, 0.12)'}
      color={cred ? '#DC2626' : '#059669'}
      title={
        cred
          ? 'Contrapartida no CRÉDITO (o débito é a conta do banco)'
          : 'Contrapartida no DÉBITO (o crédito é a conta do banco)'
      }
    >
      {lado}
    </Box>
  );
}

// Control customizado: prepende o chip D/C. Componentes estáveis (módulo) p/
// não remontar o menu a cada render.
const pocControl = (lado: 'C' | 'D') =>
  function PocControl(props: any) {
    const { children } = props;
    return (
      <chakraComponents.Control {...props}>
        <PocLadoBadge lado={lado} />
        {children}
      </chakraComponents.Control>
    );
  };

export const POC_CONTROL_C = pocControl('C');
export const POC_CONTROL_D = pocControl('D');

// Objetos estáveis (módulo) p/ não remontar o menu a cada render.
export const POC_COMPONENTS_C = { Control: POC_CONTROL_C };
export const POC_COMPONENTS_D = { Control: POC_CONTROL_D };

// Wrapper de Tooltip (Chakra, igual ao do histórico) pro select de POC. O hover
// é capturado no Box (o SingleValue do react-select tem pointer-events:none, por
// isso o tooltip não funciona dentro dele). Mostra o "codigo · nome" completo da
// contrapartida selecionada.
export function PocSelectTooltip({
  label,
  children,
}: {
  label?: string | null;
  children: React.ReactNode;
}) {
  return (
    <Tooltip label={label ?? ''} isDisabled={!label} openDelay={400} hasArrow placement="top">
      <Box w="full">{children}</Box>
    </Tooltip>
  );
}

// Estilos compartilhados do RSelect de POC (menu acima do modal/portal).
export const pocSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }),
};
